/**
 * Route aliases for navigation: screens with no menu item of their own that
 * belong to one that has.
 *
 * `activeNavItem` (composables/useNavPrefs.ts) resolves the menu item a path
 * is "on" by segment-aware longest prefix — so `/procurement/orders/42` is a
 * Purchase orders record. A screen that is reached only from INSIDE another
 * screen, at a path not beneath that screen's own `to` (a tab, a drill-down, a
 * detail page whose list lives elsewhere), matches no item at all: it would
 * show no FSD chips in its page header and highlight nothing in the sidebar.
 * Each entry here says which menu item such a screen belongs to.
 *
 * Rules (enforced by nav/coverage.test.ts):
 * - `prefix` is matched segment-aware, like a menu item's `to`, and is the
 *   static prefix of at least one real route (or route alias);
 * - `itemId` names a menu item in `NAV_PARTS`;
 * - an alias never shadows a menu item: a direct match always wins, and no
 *   prefix may already be covered by one (an alias that changes nothing is
 *   noise that hides the next real gap);
 * - `note` cites the in-app link (file:line) that opens the screen — an alias
 *   with no door is a screen the user cannot reach, not a sub-screen.
 *
 * Pure data: no imports, so the composable, the page header and the tests can
 * all read it without an import cycle.
 */

export interface NavRouteAlias {
  /** Path prefix, segment-aware (`/pricing/promotions` does not match `/pricing/promotions-x`). */
  prefix: string
  /** Id of the menu item the screen belongs to. */
  itemId: string
  /** The in-app link that reaches the screen (file:line). */
  note: string
}

/** Sorted by prefix. */
export const NAV_ROUTE_ALIASES: readonly NavRouteAlias[] = [
  {
    prefix: '/bankrec/profiles',
    itemId: 'bankrec-sessions',
    note: 'Statement mapping profiles; modules/bankrec/BankrecNav.vue:42 (tab on /bankrec/sessions, /imports, /standing-rules)',
  },
  {
    prefix: '/bankrec/standing-rules',
    itemId: 'bankrec-sessions',
    note: 'Standing match rules; modules/bankrec/BankrecNav.vue:60 (tab on /bankrec/sessions, /imports, /profiles)',
  },
  {
    prefix: '/cases',
    itemId: 'crm-cases',
    note: 'vue-router alias /cases/:id of /crm/cases/:id (modules/crm/routes.ts:76); internal/crm/sla.go links SLA-breach notifications there',
  },
  {
    prefix: '/crm/campaign-sends',
    itemId: 'crm-campaigns',
    note: 'Campaign send detail; modules/crm/c5/campaigns/CampaignView.vue:381 (campaign → send)',
  },
  {
    prefix: '/eam/task-lists',
    itemId: 'eam-maintenance',
    note: 'Maintenance task list; modules/eam/MaintenanceView.vue:401 (maintenance → task list)',
  },
  {
    prefix: '/mfg/bom-lines',
    itemId: 'mfg-boms',
    note: 'BOM line effectivity; modules/mfg/BomTreeNode.vue:153 (rendered by BomView on /mfg/boms/:id)',
  },
  {
    prefix: '/mfg/mrp-runs',
    itemId: 'mfg-mrp',
    note: 'MRP run detail; modules/mfg/MrpView.vue:560 (MRP → last run)',
  },
  {
    prefix: '/mfg/mrp-suggestions',
    itemId: 'mfg-mrp',
    note: 'MRP suggestion pegging; modules/mfg/MrpView.vue:348 (suggestion row → pegging)',
  },
  {
    prefix: '/party/consent/catalogues',
    itemId: 'party-consent',
    note: 'Consent catalogues; modules/party/PartyNav.vue:44 (tab on /party/consent/records, /party/parties, …)',
  },
  {
    prefix: '/party/loyalty/members',
    itemId: 'party-loyalty',
    note: 'Loyalty members; modules/party/loyalty/LoyaltyProgramView.vue:140 (program → members), LoyaltyMembersView.vue:176 (members → member)',
  },
  {
    prefix: '/payroll/payslips',
    itemId: 'payroll-runs',
    note: 'Payslip; modules/payroll/RunView.vue:411 (payroll run → payslip)',
  },
  {
    prefix: '/pos/day-closes',
    itemId: 'pos-day-close-board',
    note: 'POS day close; modules/pos/DayCloseBoardView.vue:259 (day-close board → close)',
  },
  {
    prefix: '/pos/menu-versions',
    itemId: 'pos-menus',
    note: 'POS menu version; modules/pos/MenusListView.vue:268 (menus → published version)',
  },
  {
    prefix: '/reporting/kpis',
    itemId: 'reporting-dashboards',
    note: 'KPI drill-down; modules/reporting/DashboardsView.vue:205 (dashboard card → drill)',
  },
  {
    prefix: '/treasury/position',
    itemId: 'treasury-positions',
    note: 'Treasury position preview; modules/treasury/position/PositionsListView.vue:151 ("open preview" on /treasury/positions)',
  },
]
